import React from 'react';
import { XIcon, CalendarIcon, ClockIcon, MapPinIcon, MonitorIcon, MusicIcon, UserIcon, BookOpenIcon, ExternalLinkIcon, CheckCircleIcon, ClockIcon as ClockOutlineIcon } from 'lucide-react';
interface StudentLesson {
  id: string;
  teacherId: string;
  title: string;
  teacherName: string;
  instrument: string;
  startAt: Date;
  durationMinutes: number;
  location: string;
  status: string;
  notes: string;
  format: string;
  meetingLink?: string;
}
interface StudentLessonDetailModalProps {
  isOpen: boolean;
  lesson: StudentLesson | null;
  onClose: () => void;
  isDark: boolean;
}
export function StudentLessonDetailModal({
  isOpen,
  lesson,
  onClose,
  isDark
}: StudentLessonDetailModalProps) {
  if (!isOpen || !lesson) return null;
  const isOnline = lesson.format?.toLowerCase() === 'online';
  // Format date
  const dateStr = lesson.startAt.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
  // Format time
  const timeStr = lesson.startAt.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  // Format end time
  const endTime = new Date(lesson.startAt.getTime() + lesson.durationMinutes * 60000);
  const endTimeStr = endTime.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  // Duration display
  const durationDisplay = lesson.durationMinutes >= 60 ? `${Math.floor(lesson.durationMinutes / 60)}h ${lesson.durationMinutes % 60 > 0 ? `${lesson.durationMinutes % 60}m` : ''}` : `${lesson.durationMinutes}m`;
  // Status display
  const getStatusDisplay = () => {
    const s = lesson.status?.toUpperCase();
    if (s === 'COMPLETED') return {
      label: 'Completed',
      color: isDark ? 'text-green-400' : 'text-green-600',
      icon: <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-2376" />
    };
    if (s === 'SCHEDULED' || s === 'CONFIRMED') return {
      label: 'Confirmed',
      color: isDark ? 'text-green-400' : 'text-green-600',
      icon: <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-2377" />
    };
    return {
      label: 'Scheduled',
      color: isDark ? 'text-blue-400' : 'text-blue-600',
      icon: <ClockOutlineIcon className="h-5 w-5 text-blue-500" data-id="element-2378" />
    };
  };
  const statusInfo = getStatusDisplay();
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-2379" />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-2380">
        <div className={`${isDark ? 'bg-navy-800' : 'bg-white'} rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col`} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="student-lesson-modal-title" data-id="element-2381">
          {/* Header */}
          <div className={`px-6 pt-6 pb-4 flex items-start justify-between flex-shrink-0`} data-id="element-2382">
            <div className="flex-1 min-w-0" data-id="element-2383">
              <h2 id="student-lesson-modal-title" className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-2384">
                {lesson.title}
              </h2>
              <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2385">
                with {lesson.teacherName}
              </p>
            </div>
            <button onClick={onClose} className={`p-2 rounded-lg transition-colors flex-shrink-0 ml-4 ${isDark ? 'hover:bg-navy-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'}`} aria-label="Close" data-id="element-2386">
              <XIcon className="h-5 w-5" data-id="element-2387" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 pb-6" data-id="element-2388">
            {/* Detail rows */}
            <div className="space-y-4 mb-6" data-id="element-2389">
              {/* Date */}
              <div className="flex items-center" data-id="element-2390">
                <div className={`p-2 rounded-lg ${isDark ? 'bg-navy-700' : 'bg-gray-100'} mr-3 flex-shrink-0`} data-id="element-2391">
                  <CalendarIcon className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2392" />
                </div>
                <div data-id="element-2393">
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2394">
                    Date
                  </p>
                  <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-2395">
                    {dateStr}
                  </p>
                </div>
              </div>

              {/* Time */}
              <div className="flex items-center" data-id="element-2396">
                <div className={`p-2 rounded-lg ${isDark ? 'bg-navy-700' : 'bg-gray-100'} mr-3 flex-shrink-0`} data-id="element-2397">
                  <ClockIcon className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2398" />
                </div>
                <div data-id="element-2399">
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2400">
                    Time
                  </p>
                  <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-2401">
                    {timeStr} – {endTimeStr}{' '}
                    <span className={`text-sm font-normal ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2402">
                      ({durationDisplay.trim()})
                    </span>
                  </p>
                </div>
              </div>

              {/* Instrument */}
              <div className="flex items-center" data-id="element-2403">
                <div className={`p-2 rounded-lg ${isDark ? 'bg-navy-700' : 'bg-gray-100'} mr-3 flex-shrink-0`} data-id="element-2404">
                  <MusicIcon className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2405" />
                </div>
                <div data-id="element-2406">
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2407">
                    Instrument
                  </p>
                  <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-2408">
                    {lesson.instrument}
                  </p>
                </div>
              </div>

              {/* Location */}
              <div className="flex items-center" data-id="element-2409">
                <div className={`p-2 rounded-lg ${isDark ? 'bg-navy-700' : 'bg-gray-100'} mr-3 flex-shrink-0`} data-id="element-2410">
                  {isOnline ? <MonitorIcon className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2411" /> : <MapPinIcon className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2412" />}
                </div>
                <div data-id="element-2413">
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2414">
                    Location
                  </p>
                  <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-2415">
                    {isOnline ? 'Online' : lesson.location || 'In-person'}
                  </p>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center" data-id="element-2416">
                <div className={`p-2 rounded-lg ${isDark ? 'bg-navy-700' : 'bg-gray-100'} mr-3 flex-shrink-0`} data-id="element-2417">
                  {statusInfo.icon}
                </div>
                <div data-id="element-2418">
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2419">
                    Status
                  </p>
                  <p className={`font-medium ${statusInfo.color}`} data-id="element-2420">
                    {statusInfo.label}
                  </p>
                </div>
              </div>

              {/* Teacher */}
              <div className="flex items-center" data-id="element-2421">
                <div className={`p-2 rounded-lg ${isDark ? 'bg-navy-700' : 'bg-gray-100'} mr-3 flex-shrink-0`} data-id="element-2422">
                  <UserIcon className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2423" />
                </div>
                <div data-id="element-2424">
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2425">
                    Teacher
                  </p>
                  <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-2426">
                    {lesson.teacherName}
                  </p>
                </div>
              </div>
            </div>

            {/* Meeting Link (if online) */}
            {isOnline && lesson.meetingLink && <div className={`p-4 rounded-lg mb-6 ${isDark ? 'bg-navy-700/50 border border-navy-600' : 'bg-blue-50 border border-blue-200'}`} data-id="element-2427">
                <div className="flex items-center justify-between" data-id="element-2428">
                  <div className="flex items-center gap-2" data-id="element-2429">
                    <MonitorIcon className={`h-4 w-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} data-id="element-2430" />
                    <span className={`text-sm font-medium ${isDark ? 'text-blue-300' : 'text-blue-700'}`} data-id="element-2431">
                      Meeting Link
                    </span>
                  </div>
                  <a href={lesson.meetingLink} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${isDark ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`} data-id="element-2432">
                    Join
                    <ExternalLinkIcon className="h-3.5 w-3.5" data-id="element-2433" />
                  </a>
                </div>
              </div>}

            {/* Notes Section */}
            <div className={`p-4 rounded-lg ${isDark ? 'bg-navy-700/50 border border-navy-600' : 'bg-gray-50 border border-gray-200'}`} data-id="element-2434">
              <div className="flex items-center mb-3" data-id="element-2435">
                <BookOpenIcon className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'} mr-2`} data-id="element-2436" />
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-2437">
                  Lesson Notes
                </h3>
              </div>
              {lesson.notes ? <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'} whitespace-pre-wrap leading-relaxed`} data-id="element-2438">
                  {lesson.notes}
                </p> : <p className={`text-sm italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-2439">
                  No notes for this lesson
                </p>}
            </div>
          </div>

          {/* Footer */}
          <div className={`px-6 py-4 border-t flex-shrink-0 ${isDark ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-2440">
            <div className="flex justify-end" data-id="element-2441">
              <button onClick={onClose} className={`px-5 py-2 rounded-lg font-medium text-sm transition-colors ${isDark ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'}`} data-id="element-2442">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>;
}